import { addExceptionNotification } from '@mse-reg/app/modules/notification.slice';
import { createAppThunk } from '@mse-reg/app/store';
import { IStudentClient, SemesterDto, StudentDto, StudentPreviewDto, UasDto } from '@mse-reg/clients';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

const searchAsync = createAppThunk(
  'studentSearch/SEARCH',
  async (
    args: { searchString: string; uas: UasDto[]; semester: SemesterDto[] },
    deps: { studentClient: IStudentClient },
    dispatch,
  ) => {
    try {
      const searchResult = await deps.studentClient.search({
        uasIds: args.uas.map((u) => u.id),
        semesterIds: args.semester.map((s) => s.id),
        searchString: args.searchString,
      });
      return { searchResult, searchString: args.searchString };
    } catch (e) {
      dispatch(addExceptionNotification(e, 'An unknown error occurred while searching.'));
      return { searchResult: [] as StudentPreviewDto[], searchString: args.searchString };
    }
  },
  (r) => ({ studentClient: r.resolve<IStudentClient>('IStudentClient') }),
);

// State
const initialState = {
  searchLoading: false,
  loadedStudent: null as StudentDto,
  searchResult: [] as StudentPreviewDto[],
  filterByUas: [] as UasDto[],
  filterBySemester: [] as SemesterDto[],
  searchString: null as string,
  resultSearchString: null as string,
};

export type StudentSearchState = Readonly<typeof initialState>;

const slice = createSlice({
  name: 'studentSearch',
  initialState,
  reducers: {
    addUasFilter(state, action: PayloadAction<UasDto>) {
      state.filterByUas = [...state.filterByUas.filter((t) => t.id !== action.payload.id), action.payload];
    },
    removeUasFilter(state, action: PayloadAction<UasDto>) {
      state.filterByUas = state.filterByUas.filter((t) => t.id !== action.payload.id);
    },
    addSemesterFilter(state, action: PayloadAction<SemesterDto>) {
      state.filterBySemester = [...state.filterBySemester.filter((t) => t.id !== action.payload.id), action.payload];
    },
    removeSemesterFilter(state, action: PayloadAction<SemesterDto>) {
      state.filterBySemester = state.filterBySemester.filter((t) => t.id !== action.payload.id);
    },
    setSearchString(state, action: PayloadAction<string>) {
      state.searchString = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(searchAsync.pending, (state) => {
        state.searchLoading = true;
      })
      .addCase(searchAsync.fulfilled, (state, action) => {
        state.searchLoading = false;
        state.searchResult = action.payload.searchResult;
        state.resultSearchString = action.payload.searchString;
      })
      .addCase(searchAsync.rejected, (state) => {
        state.searchLoading = false;
      });
  },
});

export const studentSearchReducer = slice.reducer;
export const studentSearchActions = slice.actions;
export const studentSearchThunks = { searchAsync };

// Legacy module compatibility
const initActions = (componentDispatch: any) => {
  return Object.freeze({
    addUasFilter: (uas: UasDto) => componentDispatch(slice.actions.addUasFilter(uas)),
    removeUasFilter: (uas: UasDto) => componentDispatch(slice.actions.removeUasFilter(uas)),
    addSemesterFilter: (semester: SemesterDto) => componentDispatch(slice.actions.addSemesterFilter(semester)),
    removeSemesterFilter: (semester: SemesterDto) => componentDispatch(slice.actions.removeSemesterFilter(semester)),
    async searchAsync(searchString: string, uas: UasDto[], semester: SemesterDto[]): Promise<StudentPreviewDto[]> {
      const result = await componentDispatch(searchAsync({ searchString, uas, semester }));
      return result.payload?.searchResult ?? [];
    },
    setSearchString: (searchString: string) => componentDispatch(slice.actions.setSearchString(searchString)),
  });
};

export default initActions;

export type StudentSearchActions = ReturnType<typeof initActions>;

export const studentSearchModule = {
  initActions: (dispatch: any) => initActions(dispatch),
  reducer: slice.reducer,
};
